/**
 * Pure analytics helpers computed from ActivityLog[] + User[]. Kept side-effect
 * free so they're trivial to reason about and reuse across Dashboard/Insights/
 * Timeline. Thresholds come from @mitang/shared INSIGHTS — never hardcoded.
 */
import { INSIGHTS, type AppChannel, type User } from '@mitang/shared';
import type { ActivityLogWithId } from './collections';
import { modelNumberOf, productIdOf, queryOf } from './activityMeta';
import { DAY_MS, dayKey, lastNDayKeys } from './time';

/** One derived session (app_open → next session_end for a user). */
export interface Session {
  userId: string;
  start: number;
  end: number;
  durationMs: number;
}

/**
 * Pair app_open → session_end per user to derive sessions. Logs may arrive
 * newest-first; we sort ascending per user and greedily match opens to the
 * next end. Unmatched trailing opens are ignored (still-open session).
 */
export function deriveSessions(logs: ActivityLogWithId[]): Session[] {
  const byUser = new Map<string, ActivityLogWithId[]>();
  for (const l of logs) {
    if (l.event !== 'app_open' && l.event !== 'session_end') continue;
    const arr = byUser.get(l.userId) ?? [];
    arr.push(l);
    byUser.set(l.userId, arr);
  }

  const sessions: Session[] = [];
  for (const [userId, arr] of byUser) {
    arr.sort((a, b) => a.ts - b.ts);
    let openTs: number | null = null;
    for (const l of arr) {
      if (l.event === 'app_open') {
        // A new open before a close: treat the prior open as a zero-length blip.
        openTs = l.ts;
      } else if (l.event === 'session_end' && openTs !== null) {
        sessions.push({
          userId,
          start: openTs,
          end: l.ts,
          durationMs: Math.max(0, l.ts - openTs),
        });
        openTs = null;
      }
    }
  }
  return sessions;
}

/** Count of app_open sessions per user within the given logs. */
export function sessionCountByUser(logs: ActivityLogWithId[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const l of logs) {
    if (l.event !== 'app_open') continue;
    counts.set(l.userId, (counts.get(l.userId) ?? 0) + 1);
  }
  return counts;
}

/** Activity events count per user (any event) — a coarse "engagement" score. */
export function activityCountByUser(logs: ActivityLogWithId[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const l of logs) {
    counts.set(l.userId, (counts.get(l.userId) ?? 0) + 1);
  }
  return counts;
}

/** app_open counts bucketed by local day, for the "opens over time" chart. */
export function opensByDay(
  logs: ActivityLogWithId[],
  days: number,
): { day: string; opens: number }[] {
  const buckets = new Map<string, number>();
  for (const l of logs) {
    if (l.event !== 'app_open') continue;
    const k = dayKey(l.ts);
    buckets.set(k, (buckets.get(k) ?? 0) + 1);
  }
  return lastNDayKeys(days).map((day) => ({ day, opens: buckets.get(day) ?? 0 }));
}

export interface MostActiveRow {
  user: User;
  events: number;
  sessions: number;
  lastActiveAt?: number;
}

/** Most active users this week, ranked by total events (desc). */
export function mostActiveUsers(
  users: User[],
  logs: ActivityLogWithId[],
  topN = 20,
): MostActiveRow[] {
  const events = activityCountByUser(logs);
  const sessions = sessionCountByUser(logs);
  const byId = new Map(users.map((u) => [u.id, u]));
  const rows: MostActiveRow[] = [];
  for (const [userId, count] of events) {
    const user = byId.get(userId);
    if (!user) continue;
    rows.push({
      user,
      events: count,
      sessions: sessions.get(userId) ?? 0,
      lastActiveAt: user.lastActiveAt,
    });
  }
  rows.sort((a, b) => b.events - a.events);
  return rows.slice(0, topN);
}

export interface HotLead {
  user: User;
  productId: string;
  /** What to show: the model number. Absent on events logged before it was stamped. */
  modelNumber?: string;
  viewCount: number;
}

/**
 * "Hot leads" = users who viewed the SAME product ≥ INSIGHTS.hotLeadViewCount
 * times within the window. One row per (user, product) that clears the bar,
 * highest view count first.
 */
export function hotLeads(users: User[], logs: ActivityLogWithId[]): HotLead[] {
  const byId = new Map(users.map((u) => [u.id, u]));
  // key = `${userId}|${productId}` → count
  const counts = new Map<string, number>();
  // Grouping stays on the product id (it is the unique key); the model number is
  // carried alongside purely so the UI can show something readable.
  const models = new Map<string, string>();
  for (const l of logs) {
    if (l.event !== 'viewed_product') continue;
    const pid = productIdOf(l);
    if (!pid) continue;
    const key = `${l.userId}|${pid}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
    const model = modelNumberOf(l);
    if (model && !models.has(key)) models.set(key, model);
  }

  const leads: HotLead[] = [];
  for (const [key, count] of counts) {
    if (count < INSIGHTS.hotLeadViewCount) continue;
    const [userId, productId] = key.split('|');
    const user = byId.get(userId);
    if (!user) continue;
    leads.push({ user, productId, modelNumber: models.get(key), viewCount: count });
  }
  leads.sort((a, b) => b.viewCount - a.viewCount);
  return leads;
}

export interface ChurnRow {
  user: User;
  daysInactive: number;
}

/**
 * "Churn risk" = approved users inactive ≥ INSIGHTS.churnRiskDays days.
 * Users who never had activity are included (treated as maximally inactive).
 */
export function churnRisk(users: User[]): ChurnRow[] {
  const cutoff = Date.now() - INSIGHTS.churnRiskDays * DAY_MS;
  const rows: ChurnRow[] = [];
  for (const u of users) {
    if (u.status !== 'approved') continue;
    const last = u.lastActiveAt ?? u.approvedAt ?? u.createdAt;
    if (last <= cutoff) {
      rows.push({
        user: u,
        daysInactive: Math.floor((Date.now() - last) / DAY_MS),
      });
    }
  }
  rows.sort((a, b) => b.daysInactive - a.daysInactive);
  return rows;
}

/** Top search queries within the window (for context, optional). */
export function topSearches(logs: ActivityLogWithId[], topN = 10): { query: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const l of logs) {
    if (l.event !== 'searched') continue;
    const q = queryOf(l);
    if (!q) continue;
    counts.set(q, (counts.get(q) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([query, count]) => ({ query, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, topN);
}

/* -------------------------------- wishlists ------------------------------- */

export interface WishlistSaver {
  user: User;
  /** When this dealer saved it (epoch ms), when known. */
  at?: number;
}

export interface WishlistedProduct {
  productId: string;
  savers: WishlistSaver[];
  /** Newest save across all dealers, for sorting by momentum. */
  lastSavedAt?: number;
}

/**
 * Roll the per-dealer wishlists up into a per-product view: who saved what.
 *
 * Derived ENTIRELY from the user documents the admin already holds — there is no
 * wishlist collection to query and no per-product counter document to maintain
 * (which would be a write hotspot). So this costs zero extra reads.
 */
export function wishlistedProducts(users: User[]): WishlistedProduct[] {
  const byProduct = new Map<string, WishlistedProduct>();
  for (const user of users) {
    for (const productId of user.wishlist ?? []) {
      const at = user.wishlistAt?.[productId];
      const row = byProduct.get(productId) ?? { productId, savers: [] };
      row.savers.push({ user, at });
      if (at && (!row.lastSavedAt || at > row.lastSavedAt)) row.lastSavedAt = at;
      byProduct.set(productId, row);
    }
  }
  const rows = [...byProduct.values()];
  for (const row of rows) {
    // Most recent saver first within a product.
    row.savers.sort((a, b) => (b.at ?? 0) - (a.at ?? 0));
  }
  // Most-saved first; ties broken by whichever was saved most recently.
  rows.sort(
    (a, b) => b.savers.length - a.savers.length || (b.lastSavedAt ?? 0) - (a.lastSavedAt ?? 0),
  );
  return rows;
}

/** Dealers who have saved anything, most saves first. */
export function dealersWithWishlists(users: User[]): User[] {
  return users
    .filter((u) => (u.wishlist?.length ?? 0) > 0)
    .sort((a, b) => (b.wishlist?.length ?? 0) - (a.wishlist?.length ?? 0));
}

/* ------------------------------ app versions ------------------------------ */

/**
 * The install that represents an account: Play beats sideload beats browser.
 * Each lives in its own `installs` slot, so a dealer with the Play app who also
 * opens the website stays a Play install (the browser visit is reported
 * separately via `installs.web`).
 */
export interface PrimaryInstall {
  channel: AppChannel;
  build?: number;
  version?: string;
  seenAt: number;
}

export function primaryInstall(u: User): PrimaryInstall | null {
  const i = u.installs;
  if (!i) return null;
  if (i.play) return { channel: 'play', ...i.play };
  if (i.sideload) return { channel: 'sideload', ...i.sideload };
  if (i.web) return { channel: 'web', seenAt: i.web.seenAt };
  return null;
}

/**
 * Where one account stands relative to the current Play release:
 *  - latest    Play install at/above app_config.apk.playVersionCode (or no
 *              marker published yet — nothing newer exists to be behind)
 *  - behind    Play install below the marker → sees "Update from Play Store"
 *  - sideload  legacy APK channel (updated by the sideload gate, not Play)
 *  - web       only ever opened in a browser — no build to compare
 *  - unknown   has not opened the app since build tracking began
 */
export type AppVersionState = 'latest' | 'behind' | 'sideload' | 'web' | 'unknown';

export function appVersionState(u: User, latestPlayBuild?: number): AppVersionState {
  const p = primaryInstall(u);
  switch (p?.channel) {
    case 'play':
      if (latestPlayBuild === undefined) return 'latest';
      return (p.build ?? 0) >= latestPlayBuild ? 'latest' : 'behind';
    case 'sideload':
      return 'sideload';
    case 'web':
      return 'web';
    default:
      return 'unknown';
  }
}

/** One row of the "App versions" breakdown (dashboard card + App versions page). */
export interface AppVersionRow {
  key: string;
  channel: AppChannel | 'unknown';
  build?: number;
  version?: string;
  count: number;
  /** Play installs on the newest published Play build. */
  latest: boolean;
}

export interface AppVersionSummary {
  /** Latest Play first, then newer builds before older, then sideload, browser, unrecorded. */
  rows: AppVersionRow[];
  /** Accounts whose primary install is the Play app. */
  playTotal: number;
  playOnLatest: number;
  playBehind: number;
  sideload: number;
  web: number;
  /** Accounts that have not opened the app since build tracking began. */
  unrecorded: number;
}

const CHANNEL_ORDER: Record<AppChannel | 'unknown', number> = {
  play: 0,
  sideload: 1,
  web: 2,
  unknown: 3,
};

/** Stable grouping key for an account's primary install — the key an AppVersionRow carries. */
export function appVersionKey(u: User): string {
  const p = primaryInstall(u);
  if (!p) return 'unknown:';
  return `${p.channel}:${p.channel === 'web' ? '' : (p.build ?? '')}`;
}

/**
 * Group accounts by their primary install. `latestPlayBuild` is
 * app_config.apk.playVersionCode, published by scripts/mark-play-live.mjs once
 * Google approves a rollout, so "on latest" needs no hardcoded number.
 */
export function appVersionSummary(users: User[], latestPlayBuild?: number): AppVersionSummary {
  const rows = new Map<string, AppVersionRow>();
  const totals = { playTotal: 0, playOnLatest: 0, playBehind: 0, sideload: 0, web: 0, unrecorded: 0 };

  for (const u of users) {
    const p = primaryInstall(u);
    const state = appVersionState(u, latestPlayBuild);
    const channel: AppChannel | 'unknown' = p?.channel ?? 'unknown';
    const build = p && p.channel !== 'web' ? p.build : undefined;
    if (channel === 'play') totals.playTotal += 1;
    if (state === 'latest') totals.playOnLatest += 1;
    else if (state === 'behind') totals.playBehind += 1;
    else if (state === 'sideload') totals.sideload += 1;
    else if (state === 'web') totals.web += 1;
    else totals.unrecorded += 1;

    const key = appVersionKey(u);
    const row = rows.get(key) ?? {
      key,
      channel,
      count: 0,
      latest: state === 'latest',
      ...(build !== undefined ? { build } : {}),
      ...(p?.version && build !== undefined ? { version: p.version } : {}),
    };
    row.count += 1;
    rows.set(key, row);
  }

  const sorted = [...rows.values()].sort((a, b) => {
    if (a.latest !== b.latest) return a.latest ? -1 : 1;
    const ch = CHANNEL_ORDER[a.channel] - CHANNEL_ORDER[b.channel];
    if (ch !== 0) return ch;
    return (b.build ?? 0) - (a.build ?? 0);
  });

  return { rows: sorted, ...totals };
}
